import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { FiPlus, FiTarget, FiEye, FiRefreshCw } from 'react-icons/fi';
import ContentFocusModal from '../components/ContentFocusModal';
import GenerateMoreModal from '../components/GenerateMoreModal';

function CampaignDetail() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [client, setClient] = useState(null);
  const [focuses, setFocuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFocusModal, setShowFocusModal] = useState(false);
  const [showGenerateMoreModal, setShowGenerateMoreModal] = useState(false);
  const [selectedFocus, setSelectedFocus] = useState(null);
  // For "Fresh Angle" - opens ContentFocusModal at Step 2 with pre-loaded photo
  const [freshAngleMode, setFreshAngleMode] = useState(false);

  useEffect(() => {
    loadCampaign();
  }, [campaignId]);

  const loadCampaign = async () => {
    try {
      const campaignData = await api.getCampaign(campaignId);
      setCampaign(campaignData);
      
      const clientData = await api.getClient(campaignData.client_id);
      setClient(clientData);

      const focusesData = await api.listContentFocuses(campaignId);
      setFocuses(focusesData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFocusClick = (focus) => {
    setSelectedFocus(focus);
    setShowGenerateMoreModal(true);
  };
  
  // Handle "Fresh Angle" - opens ContentFocusModal with photo pre-loaded
  const handleFreshAngle = () => {
    setFreshAngleMode(true);
    setShowFocusModal(true);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-[60vh]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-muted-foreground text-sm">Loading campaign...</p>
      </div>
    </div>
  );

  if (!campaign) return <div className="text-foreground p-8">Campaign not found</div>;

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
          <button onClick={() => navigate(`/clients/${campaign.client_id}`)} className="hover:text-primary transition-colors">
            {client?.name}
          </button>
          <span>/</span>
          <span className="text-foreground">{campaign.name}</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">{campaign.name}</h1>
        <p className="text-muted-foreground">{campaign.objective.replace('_', ' ')}</p>
      </div>

      <div className="bg-card rounded-xl p-6 border border-border/50">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-accent/10">
            <FiTarget className="text-accent" size={24} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-2">Creative Angle</h3>
            <h4 className="text-lg text-primary font-semibold mb-2">{campaign.creative_angle}</h4>
            <p className="text-muted-foreground mb-4">{campaign.creative_angle_description}</p>
            <div className="flex flex-wrap gap-2">
              {campaign.platforms.map((platform, idx) => (
                <span key={idx} className="px-3 py-1 bg-accent/10 text-accent rounded-full text-sm">
                  {platform}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-heading font-semibold text-foreground">Content Directions</h2>
          <button
            onClick={() => setShowFocusModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-all"
            data-testid="create-content-btn"
          >
            <FiPlus size={18} />
            <span>Create Content</span>
          </button>
        </div>

        {focuses.length === 0 ? (
          <div className="bg-card rounded-xl p-12 text-center border border-border/50">
            <div className="max-w-md mx-auto">
              <h3 className="text-lg font-heading font-semibold text-foreground mb-2">Ready to Create Content</h3>
              <p className="text-muted-foreground mb-6">
                Define a creative direction and generate content ideas with images in one seamless flow.
              </p>
              <button
                onClick={() => setShowFocusModal(true)}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-all"
              >
                Create Content
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {focuses.map(focus => (
              <div
                key={focus.id}
                className="bg-card rounded-xl p-6 border border-border/50 hover:border-primary/30 transition-all hover-lift"
              >
                <h3 className="text-lg font-semibold text-foreground mb-2">{focus.name}</h3>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{focus.description}</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleFocusClick(focus)}
                    className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-all flex items-center justify-center gap-2"
                    data-testid={`generate-more-${focus.id}-btn`}
                  >
                    <FiRefreshCw size={16} />
                    Generate More
                  </button>
                  <button
                    onClick={() => navigate('/content')}
                    className="px-4 py-2 border border-border text-muted-foreground rounded-lg hover:bg-secondary hover:text-foreground transition-colors"
                  >
                    <FiEye size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showFocusModal && client && (
        <ContentFocusModal
          campaignId={campaignId}
          campaign={campaign}
          client={client}
          // If freshAngleMode and selectedFocus, pass the focus to pre-load photo
          preloadedFocus={freshAngleMode ? selectedFocus : null}
          onClose={() => {
            setShowFocusModal(false);
            setFreshAngleMode(false);
          }}
          onSuccess={() => {
            setShowFocusModal(false);
            setFreshAngleMode(false);
            navigate('/content');
          }}
        />
      )}

      {showGenerateMoreModal && selectedFocus && client && (
        <GenerateMoreModal
          focus={selectedFocus}
          campaign={campaign}
          client={client}
          onClose={() => {
            setShowGenerateMoreModal(false);
            setSelectedFocus(null);
          }}
          onSuccess={() => {
            setShowGenerateMoreModal(false);
            setSelectedFocus(null);
            navigate('/content');
          }}
          onFreshAngle={() => {
            // Close GenerateMore, open ContentFocusModal with focus pre-loaded
            setShowGenerateMoreModal(false);
            handleFreshAngle();
          }}
        />
      )}
    </div>
  );
}

export default CampaignDetail;
